import React, { useState } from 'react';
import { InfoIcon } from './Icons';

// Tooltip-style popover for explanations
const ExplanationPopover = ({ open, title, children, anchorRef }) => {
  if (!open || !anchorRef?.current) return null;
  const rect = anchorRef.current.getBoundingClientRect();
  const popoverWidth = 320; // maxWidth
  const minMargin = 8;
  let left = rect.left + rect.width / 2 - popoverWidth / 2;
  // Clamp left so popover stays in viewport
  if (left < minMargin) left = minMargin;
  if (left + popoverWidth > window.innerWidth - minMargin) left = window.innerWidth - minMargin - popoverWidth;
  const style = {
    position: 'fixed',
    top: rect.bottom + 6,
    left: left,
    zIndex: 9999,
    background: 'var(--background, #23293a)',
    color: 'var(--text, #fff)',
    border: '1px solid var(--border, #444)',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    padding: '0.7rem 1rem',
    minWidth: 220,
    maxWidth: popoverWidth,
    fontSize: 14,
    pointerEvents: 'auto',
  };
  return (
    <div style={style}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div>{children}</div>
    </div>
  );
};

const COUNTER_EXPLANATIONS = [
  {
    key: 'highRiskEmployeeInfections',
    label: 'High-Risk Infections',
    explanation: 'Number of primary domain employee credentials that were found in information stealer logs and are classified as high risk (risk score 3 or above) using heuristics. This means that those credentials have the potential for high access to your company’s data/systems.',
  },
  {
    key: 'personalComputerInfections',
    label: 'Employee Personal Infections',
    explanation: 'Number of information stealer infections detected for employees using personal computers(classified using heuristics) on primary domains.',
  },
  {
    key: 'corporateComputerInfections',
    label: 'Employee Corporate Infections',
    explanation: 'Number of information stealer infections detected for employees using corporate computers(classified using heuristics) on primary domains.',
  },
  {
    key: 'thirdPartyInfoStealers',
    label: 'Third-Party Infections',
    explanation: 'Number of information stealer infections detected for third-party domains associated with your organization.',
  },
  {
    key: 'externalPageCredentials',
    label: 'Employee in External Website',
    explanation: 'Number of employee credentials being used for external (non-company related) websites. This means that the employee is using the company email address for personal use. May be high risk if credentials are the same as the ones used for the company and means that the compliance is not being followed.',
  },
  {
    key: 'outsourcingInfections',
    label: 'Outsourcing Infections',
    explanation: 'Number of infections detected for outsourced or contractor accounts.',
  },
  {
    key: 'attackerInfections',
    label: 'Attacker Infections',
    explanation: 'Number of information stealer infections detected for records classified as ATTACKER computer type. These may represent attacker-controlled or adversarial systems.',
  },
  {
    key: 'userInfections',
    label: 'User Infections',
    explanation: 'Number of information stealer infections detected for user accounts.',
  },
];

// DashboardCounters component
const DashboardCounters = ({
  highRiskEmployeeInfections,
  personalComputerInfections,
  corporateComputerInfections,
  thirdPartyInfoStealers,
  externalPageCredentials,
  outsourcingInfections,
  attackerInfections,
  userInfections,
  onCounterClick,
  activeCounterKey,
  highRiskEmployeeInfectionsUnsolved,
  personalComputerInfectionsUnsolved,
  corporateComputerInfectionsUnsolved,
  attackerInfectionsUnsolved,
  thirdPartyInfoStealersUnsolved,
  externalPageCredentialsUnsolved,
  outsourcingInfectionsUnsolved,
  userInfectionsUnsolved
}) => {
  const [popoverKey, setPopoverKey] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const popoverTimeout = React.useRef();
  const iconRefs = React.useRef({});
  // Handlers for hover/focus
  const showPopover = (key) => {
    clearTimeout(popoverTimeout.current);
    setPopoverKey(key);
    setPopoverAnchor(iconRefs.current[key]);
  };
  const hidePopover = () => {
    popoverTimeout.current = setTimeout(() => {
      setPopoverKey(null);
      setPopoverAnchor(null);
    }, 120);
  };
  const cancelHidePopover = () => {
    clearTimeout(popoverTimeout.current);
  };
  const counters = [
    { 
      key: 'highRiskEmployeeInfections', 
      value: highRiskEmployeeInfections,
      unsolved: highRiskEmployeeInfectionsUnsolved
    },
    { 
      key: 'corporateComputerInfections', 
      value: corporateComputerInfections,
      unsolved: corporateComputerInfectionsUnsolved
    },
    { 
      key: 'personalComputerInfections', 
      value: personalComputerInfections,
      unsolved: personalComputerInfectionsUnsolved
    },
    { 
      key: 'externalPageCredentials', 
      value: externalPageCredentials,
      unsolved: externalPageCredentialsUnsolved
    },
    { 
      key: 'userInfections', 
      value: userInfections,
      unsolved: userInfectionsUnsolved
    },
    { 
      key: 'thirdPartyInfoStealers', 
      value: thirdPartyInfoStealers,
      unsolved: thirdPartyInfoStealersUnsolved
    },
    { 
      key: 'attackerInfections', 
      value: attackerInfections,
      unsolved: attackerInfectionsUnsolved
    },
  ];
  return (
    <>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'clamp(1rem, 2vw, 2rem)',
        background: 'none',
        boxShadow: 'none',
        border: 'none',
        padding: '2px',
        width: '100%',
        maxWidth: '100%',
        overflow: 'visible',
      }}>
        {counters.map((counter, idx) => {
          const info = COUNTER_EXPLANATIONS.find(e => e.key === counter.key);
          const isActive = activeCounterKey === counter.key;
          return (
            <React.Fragment key={counter.key}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 'clamp(80px, 8vw, 110px)',
                  flex: 1,
                  position: 'relative',
                  cursor: 'pointer',
                  background: isActive ? 'rgba(255, 204, 000, 0.3)' : 'none',
                  borderRadius: isActive ? 8 : undefined,
                  padding: isActive ? '8px 6px' : '8px 6px',
                  boxShadow: isActive ? '0 0 0 2px var(--primary-color, #007bff)' : undefined,
                  transition: 'background 0.15s, box-shadow 0.15s',
                }}
                onClick={() => onCounterClick && onCounterClick(counter.key)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onCounterClick && onCounterClick(counter.key);
                  }
                }}
                tabIndex={0}
                title={`Filter table by ${info.label}`}
              >
                <div style={{ position: 'relative' }}>
                  <span style={{ fontSize: 'clamp(1.2rem, 3vw, 2rem)', fontWeight: 700, color: counter.key === 'highRiskEmployeeInfections' ? 'var(--primary-color)' : 'var(--text)', lineHeight: 1 }}>{counter.value}</span>
                  {counter.unsolved > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -8,
                      right: -12,
                      background: 'var(--primary-color, #e57373)',
                      color: 'var(--secondary-color, #fff)',
                      fontSize: 'clamp(0.6rem, 1.2vw, 0.7rem)',
                      fontWeight: 700,
                      borderRadius: '50%',
                      width: 'clamp(16px, 2.5vw, 20px)',
                      height: 'clamp(16px, 2.5vw, 20px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      minWidth: 'clamp(16px, 2.5vw, 20px)',
                      minHeight: 'clamp(16px, 2.5vw, 20px)',
                    }}>
                      {counter.unsolved > 99 ? '99+' : counter.unsolved}
                    </span>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  fontSize: 'clamp(0.6rem, 1.5vw, 0.8rem)',
                  color: 'var(--text-light)',
                  fontWeight: 500,
                  textAlign: 'center',
                  marginTop: 2,
                  lineHeight: 1.2,
                }}>
                  <span style={{ display: 'inline-block', width: 16, height: 1, visibility: 'hidden' }} />
                  <span>
                    {info.label.split(' ').length > 1
                      ? info.label.split(' ').map((word, i, arr) =>
                          i === Math.floor(arr.length / 2) ? <React.Fragment key={i}><br />{word} </React.Fragment> : word + ' '
                        )
                      : info.label}
                  </span>
                  <span
                    ref={el => (iconRefs.current[counter.key] = el)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '16px',
                      width: '16px',
                      margin: '0 0 1px 3px',
                      cursor: 'pointer',
                    }}
                    aria-label={`Explain ${info.label}`}
                    tabIndex={0}
                    onMouseEnter={() => showPopover(counter.key)}
                    onMouseLeave={hidePopover}
                    onFocus={() => showPopover(counter.key)}
                    onBlur={hidePopover}
                  >
                    <InfoIcon size={11} color={'var(--primary-color)'} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </span>
                </div>
              </div>
              {idx < counters.length - 1 && (
                <div style={{ width: 1, height: 'clamp(40px, 8vw, 54px)', background: 'var(--primary-color)', alignSelf: 'center' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <ExplanationPopover
        open={!!popoverKey}
        title={COUNTER_EXPLANATIONS.find(e => e.key === popoverKey)?.label}
        anchorRef={popoverAnchor ? { current: popoverAnchor } : null}
      >
        <div
          onMouseEnter={cancelHidePopover}
          onMouseLeave={hidePopover}
        >
          {COUNTER_EXPLANATIONS.find(e => e.key === popoverKey)?.explanation}
        </div>
      </ExplanationPopover>
    </>
  );
};

export default DashboardCounters; 